/**
 * Utility functions for processing and aggregating filter options from historical race data
 * Supports bulk analysis filtering requirements
 */

/**
 * Aggregate race data by various dimensions for filter option generation
 * @param {Array} races - Array of race data
 * @returns {Object} Aggregated data for filter options
 */
export const aggregateRaceDataForFilters = (races) => {
  if (!races || !Array.isArray(races)) {
    return getEmptyAggregation();
  }

  const aggregation = {
    dateStats: new Map(),
    stateStats: new Map(),
    trackStats: new Map(),
    dayOfWeekStats: new Map(),
    horseCountStats: new Map(),
    oddsRangeStats: new Map(),
    raceTypeStats: new Map(),
    monthlyStats: new Map(),
    yearlyStats: new Map(),
  };

  races.forEach(race => {
    try {
      // Process date-related aggregations
      if (race.date) {
        const raceDate = new Date(race.date);
        const dateStr = race.date.split('T')[0];
        const dayOfWeek = raceDate.getDay();
        const month = raceDate.getMonth();
        const year = raceDate.getFullYear();
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];

        // Date stats
        incrementMapValue(aggregation.dateStats, dateStr);
        
        // Day of week stats
        incrementMapValue(aggregation.dayOfWeekStats, dayNames[dayOfWeek]);
        
        // Monthly stats
        incrementMapValue(aggregation.monthlyStats, `${year}-${String(month + 1).padStart(2, '0')}`);
        
        // Yearly stats
        incrementMapValue(aggregation.yearlyStats, year.toString());
      }

      // Process location-related aggregations
      if (race.state) {
        incrementMapValue(aggregation.stateStats, race.state);
      }

      if (race.track) {
        incrementMapValue(aggregation.trackStats, race.track);
      }

      // Process race characteristics
      const horseCount = race.horse_count || race.horses?.length || 0;
      if (horseCount > 0) {
        incrementMapValue(aggregation.horseCountStats, horseCount.toString());
      }

      // Process race type
      if (race.race_type || race.type) {
        incrementMapValue(aggregation.raceTypeStats, race.race_type || race.type);
      }

      // Process odds ranges for combined odds analysis
      if (race.tips && Array.isArray(race.tips)) {
        const combinedOdds = calculateCombinedOdds(race.tips);
        const oddsRange = getOddsRange(combinedOdds);
        incrementMapValue(aggregation.oddsRangeStats, oddsRange);
      }
    } catch (error) {
      console.warn('Error processing race for aggregation:', race.id, error);
    }
  });

  return aggregation;
};

/**
 * Generate comprehensive filter options from aggregated data
 * @param {Object} aggregation - Aggregated race data
 * @param {Object} options - Generation options
 * @returns {Object} Comprehensive filter options
 */
export const generateFilterOptionsFromAggregation = (aggregation, options = {}) => {
  const {
    includeStats = true,
    sortByFrequency = true,
    minOccurrences = 1,
  } = options;

  try {
    // Convert maps to sorted arrays with optional statistics
    const dateOptions = mapToSortedArray(aggregation.dateStats, { 
      sortByFrequency: false, // Dates should be chronological
      reverse: true, // Most recent first
      includeStats,
      minOccurrences 
    });

    const stateOptions = mapToSortedArray(aggregation.stateStats, { 
      sortByFrequency, 
      includeStats,
      minOccurrences 
    });

    const trackOptions = mapToSortedArray(aggregation.trackStats, { 
      sortByFrequency, 
      includeStats,
      minOccurrences 
    });

    const dayOfWeekOptions = mapToSortedArray(aggregation.dayOfWeekStats, { 
      sortByFrequency: false, // Keep logical order
      customOrder: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      includeStats,
      minOccurrences 
    });

    const raceTypeOptions = mapToSortedArray(aggregation.raceTypeStats, { 
      sortByFrequency, 
      includeStats,
      minOccurrences 
    });

    // Calculate date range
    const dates = Array.from(aggregation.dateStats.keys()).sort();
    const dateRange = {
      earliest: dates[0] || new Date().toISOString().split('T')[0],
      latest: dates[dates.length - 1] || new Date().toISOString().split('T')[0]
    };

    // Calculate horse count range
    const horseCounts = Array.from(aggregation.horseCountStats.keys())
      .map(count => parseInt(count))
      .filter(count => !isNaN(count))
      .sort((a, b) => a - b);

    const horseCountRange = {
      min: horseCounts[0] || 4,
      max: horseCounts[horseCounts.length - 1] || 20
    };

    // Generate monthly and yearly options for advanced filtering
    const monthlyOptions = mapToSortedArray(aggregation.monthlyStats, { 
      sortByFrequency: false,
      reverse: true,
      includeStats,
      minOccurrences 
    });

    const yearlyOptions = mapToSortedArray(aggregation.yearlyStats, { 
      sortByFrequency: false,
      reverse: true,
      includeStats,
      minOccurrences 
    });

    return {
      availableDates: dateOptions,
      availableStates: stateOptions,
      availableTracks: trackOptions,
      availableDaysOfWeek: dayOfWeekOptions,
      availableRaceTypes: raceTypeOptions,
      availableMonths: monthlyOptions,
      availableYears: yearlyOptions,
      dateRange,
      horseCountRange,
      totalRaces: Array.from(aggregation.dateStats.values()).reduce((sum, count) => sum + count, 0),
      dataQuality: assessDataQuality(aggregation),
    };
  } catch (error) {
    console.error('Error generating filter options:', error);
    return getEmptyFilterOptions();
  }
};

/**
 * Validate filter criteria against available options
 * @param {Object} filters - Filter criteria to validate
 * @param {Object} availableOptions - Available filter options
 * @returns {Object} Validation result with errors and warnings
 */
export const validateFilterCriteria = (filters, availableOptions) => {
  const validation = {
    isValid: true,
    errors: [],
    warnings: [],
    sanitizedFilters: { ...filters }
  };

  try {
    // Validate date range
    if (filters.dateRange) {
      const { start, end } = filters.dateRange;
      const { earliest, latest } = availableOptions.dateRange || {};

      if (start && earliest && start < earliest) {
        validation.warnings.push(`Start date ${start} is before earliest available date ${earliest}`);
        validation.sanitizedFilters.dateRange.start = earliest;
      }

      if (end && latest && end > latest) {
        validation.warnings.push(`End date ${end} is after latest available date ${latest}`);
        validation.sanitizedFilters.dateRange.end = latest;
      }

      if (start && end && start > end) {
        validation.errors.push('Start date cannot be after end date');
        validation.isValid = false;
      }
    }

    // Validate states
    if (filters.states && Array.isArray(filters.states)) {
      const availableStates = availableOptions.availableStates?.map(s => s.value || s) || [];
      const invalidStates = filters.states.filter(state => !availableStates.includes(state));
      
      if (invalidStates.length > 0) {
        validation.warnings.push(`Invalid states: ${invalidStates.join(', ')}`);
        validation.sanitizedFilters.states = filters.states.filter(state => availableStates.includes(state));
      }
    }

    // Validate tracks
    if (filters.tracks && Array.isArray(filters.tracks)) {
      const availableTracks = availableOptions.availableTracks?.map(t => t.value || t) || [];
      const invalidTracks = filters.tracks.filter(track => !availableTracks.includes(track));
      
      if (invalidTracks.length > 0) {
        validation.warnings.push(`Invalid tracks: ${invalidTracks.join(', ')}`);
        validation.sanitizedFilters.tracks = filters.tracks.filter(track => availableTracks.includes(track));
      }
    }

    // Validate horse count range
    if (filters.horseCountRange) {
      const { min, max } = filters.horseCountRange;
      const availableRange = availableOptions.horseCountRange || {};

      if (min !== null && min < (availableRange.min || 0)) {
        validation.warnings.push(`Minimum horse count ${min} is below available minimum ${availableRange.min}`);
        validation.sanitizedFilters.horseCountRange.min = availableRange.min;
      }

      if (max !== null && max > (availableRange.max || 100)) {
        validation.warnings.push(`Maximum horse count ${max} is above available maximum ${availableRange.max}`);
        validation.sanitizedFilters.horseCountRange.max = availableRange.max;
      }

      if (min !== null && max !== null && min > max) {
        validation.errors.push('Minimum horse count cannot be greater than maximum');
        validation.isValid = false;
      }
    }

    // Validate combined odds threshold
    if (filters.combinedOddsThreshold !== null && filters.combinedOddsThreshold !== undefined) {
      if (filters.combinedOddsThreshold < 0) {
        validation.errors.push('Combined odds threshold cannot be negative');
        validation.isValid = false;
      }

      if (filters.combinedOddsThreshold > 10) {
        validation.warnings.push('Combined odds threshold is very high, may return few results');
      }
    }

  } catch (error) {
    console.error('Error validating filter criteria:', error);
    validation.errors.push('Validation error occurred');
    validation.isValid = false;
  }

  return validation;
};

/**
 * Sanitize and normalize filter input data
 * @param {Object} rawFilters - Raw filter input
 * @returns {Object} Sanitized filter object
 */
export const sanitizeFilterInput = (rawFilters) => {
  if (!rawFilters || typeof rawFilters !== 'object') {
    return getDefaultFilters();
  }

  const sanitized = {};

  // Sanitize date range
  if (rawFilters.dateRange && typeof rawFilters.dateRange === 'object') {
    sanitized.dateRange = {
      start: sanitizeDate(rawFilters.dateRange.start),
      end: sanitizeDate(rawFilters.dateRange.end)
    };
  }

  // Sanitize array filters
  sanitized.daysOfWeek = sanitizeStringArray(rawFilters.daysOfWeek);
  sanitized.states = sanitizeStringArray(rawFilters.states);
  sanitized.tracks = sanitizeStringArray(rawFilters.tracks);
  sanitized.raceTypes = sanitizeStringArray(rawFilters.raceTypes);

  // Sanitize numeric filters
  sanitized.combinedOddsThreshold = sanitizeNumber(rawFilters.combinedOddsThreshold);

  // Sanitize horse count range
  if (rawFilters.horseCountRange && typeof rawFilters.horseCountRange === 'object') {
    sanitized.horseCountRange = {
      min: sanitizeNumber(rawFilters.horseCountRange.min, { min: 1, max: 50 }),
      max: sanitizeNumber(rawFilters.horseCountRange.max, { min: 1, max: 50 })
    };
  }

  return sanitized;
};

// Helper functions

const incrementMapValue = (map, key) => {
  map.set(key, (map.get(key) || 0) + 1);
};

const calculateCombinedOdds = (tips) => {
  return tips.reduce((sum, tip) => {
    const odds = parseFloat(tip.odds) || 0;
    return sum + (odds > 0 ? 1 / odds : 0);
  }, 0);
};

const getOddsRange = (combinedOdds) => {
  if (combinedOdds <= 0.5) return 'very-low';
  if (combinedOdds <= 1.0) return 'low';
  if (combinedOdds <= 2.0) return 'medium';
  if (combinedOdds <= 4.0) return 'high';
  return 'very-high';
};

const mapToSortedArray = (map, options = {}) => {
  const {
    sortByFrequency = true,
    reverse = false,
    includeStats = false,
    minOccurrences = 1,
    customOrder = null
  } = options;

  let entries = Array.from(map.entries())
    .filter(([, count]) => count >= minOccurrences);

  if (customOrder) {
    entries.sort(([a], [b]) => {
      const indexA = customOrder.indexOf(a);
      const indexB = customOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  } else if (sortByFrequency) {
    entries.sort(([, a], [, b]) => b - a);
  } else {
    entries.sort(([a], [b]) => a.localeCompare(b));
  }

  if (reverse) {
    entries.reverse();
  }

  return entries.map(([value, count]) => 
    includeStats 
      ? { value, count, percentage: ((count / map.size) * 100).toFixed(1) }
      : value
  );
};

const assessDataQuality = (aggregation) => {
  const totalRaces = Array.from(aggregation.dateStats.values()).reduce((sum, count) => sum + count, 0);
  
  return {
    totalRaces,
    datesCovered: aggregation.dateStats.size,
    statesCovered: aggregation.stateStats.size,
    tracksCovered: aggregation.trackStats.size,
    completenessScore: calculateCompletenessScore(aggregation),
    lastUpdated: new Date().toISOString()
  };
};

const calculateCompletenessScore = (aggregation) => {
  let score = 0;
  let maxScore = 0;

  // Check date coverage (20 points)
  maxScore += 20;
  if (aggregation.dateStats.size > 0) score += 20;

  // Check state coverage (20 points)
  maxScore += 20;
  const expectedStates = 8; // Australian states + territories
  score += Math.min(20, (aggregation.stateStats.size / expectedStates) * 20);

  // Check track coverage (20 points)
  maxScore += 20;
  if (aggregation.trackStats.size > 10) score += 20;
  else if (aggregation.trackStats.size > 5) score += 15;
  else if (aggregation.trackStats.size > 0) score += 10;

  // Check data consistency (40 points)
  maxScore += 40;
  const totalRaces = Array.from(aggregation.dateStats.values()).reduce((sum, count) => sum + count, 0);
  const racesWithHorseCounts = Array.from(aggregation.horseCountStats.values()).reduce((sum, count) => sum + count, 0);
  
  if (totalRaces > 0) {
    const consistencyRatio = racesWithHorseCounts / totalRaces;
    score += consistencyRatio * 40;
  }

  return Math.round((score / maxScore) * 100);
};

const getEmptyAggregation = () => ({
  dateStats: new Map(),
  stateStats: new Map(),
  trackStats: new Map(),
  dayOfWeekStats: new Map(),
  horseCountStats: new Map(),
  oddsRangeStats: new Map(),
  raceTypeStats: new Map(),
  monthlyStats: new Map(),
  yearlyStats: new Map(),
});

const getEmptyFilterOptions = () => ({
  availableDates: [],
  availableStates: [],
  availableTracks: [],
  availableDaysOfWeek: [],
  availableRaceTypes: [],
  availableMonths: [],
  availableYears: [],
  dateRange: {
    earliest: new Date().toISOString().split('T')[0],
    latest: new Date().toISOString().split('T')[0]
  },
  horseCountRange: { min: 4, max: 20 },
  totalRaces: 0,
  dataQuality: { totalRaces: 0, completenessScore: 0 }
});

const getDefaultFilters = () => ({
  dateRange: { start: null, end: null },
  daysOfWeek: [],
  states: [],
  tracks: [],
  raceTypes: [],
  combinedOddsThreshold: null,
  horseCountRange: { min: null, max: null }
});

const sanitizeDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateRegex.test(dateStr) ? dateStr : null;
};

const sanitizeStringArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter(item => typeof item === 'string' && item.trim().length > 0);
};

const sanitizeNumber = (num, options = {}) => {
  const { min = null, max = null } = options;
  
  if (num === null || num === undefined || num === '') return null;
  
  const parsed = parseFloat(num);
  if (isNaN(parsed)) return null;
  
  if (min !== null && parsed < min) return min;
  if (max !== null && parsed > max) return max;
  
  return parsed;
};